// Task commands: (ctx, input) → { writes, ...result }. Pure — the caller commits the writes as one
// group. ctx = { tasks, sections, sessions, timer, clock, lock, now, newId } (only what a command reads).
//   tasks     all task docs            sessions  clockSessions docs (for focusedTime on bank)
//   sections  all section docs         lock / timer / clock  the meta docs (may be null)
import { makeTaskDoc } from '../taskdoc.js';
import { computeTaskIndices, planTaskMove } from '../indexing.js';
import { makeTimerState } from '../timer.js';
import { localToday, localDateStr } from '../time.js';
import { del, set, update, mergeUpdates } from './writes.js';
import { stampEntryDepartment } from '../workdept.js';
import { askOnComplete } from '../metrics.js';
import { captureWrites } from './metrics.js';
import { isVoidTask, restoreTaskWrites } from '../projectclose.js';

const TASK = (id) => `tasks/${id}`;
const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());
const find = (ctx, id) => (ctx.tasks || []).find((t) => t.id === id) || null;
const need = (ctx, id) => { const t = find(ctx, id); if (!t) throw new Error(`no task ${id}`); return t; };

/** A new task. `input` = the fields (title required); id from ctx.newId unless given. */
export function addTask(ctx, input) {
  if (!input?.title || !String(input.title).trim()) throw new Error('title required');
  const task = makeTaskDoc({ ...input, title: String(input.title).trim() }, ctx.tasks || [], now(ctx), ctx.newId);
  return { writes: [set(TASK(task.id), task)], task };
}

/** Several tasks in one group, sequential sortOrder; each may reference an earlier one as parentId. */
export function addTasks(ctx, list) {
  const at = now(ctx);
  let scope = ctx.tasks || [];
  const tasks = [];
  for (const input of list || []) {
    if (!input?.title) continue;
    const t = makeTaskDoc(input, scope, at + tasks.length, ctx.newId);
    scope = [...scope, t];
    tasks.push(t);
  }
  return { writes: tasks.map((t) => set(TASK(t.id), t)), tasks };
}

const EDITABLE = new Set(['clientOk', 'title', 'description', 'priority', 'scheduledDate', 'scheduledTime', 'plannedStart', 'remindAt', 'remindTime', 'remindStyle', 'remindDismissedAt', 'opens', 'timeBlock', 'estimatedMinutes', 'projectId', 'sectionId', 'parentId', 'tags', 'notes', 'billable', 'requiresAfterPhoto', 'moneyEarned', 'actualValue', 'taskType', 'saleStatus', 'manualOrder', 'sortOrder', 'status', 'completedAt', 'photo', 'shots', 'afterPhotos', 'afterPhoto', 'photoCoords', 'isServiceItem', 'skippedCount', 'lastSkippedAt', 'blockedByTaskId', 'bucket', 'bucketDate', 'expenseCategory', 'voiceNote', 'assignee', 'review', 'departmentId', 'departmentGuess', 'noteOnDone', 'requiresBeforePhoto', 'piece', 'qty', 'unit', 'catalogItemId', 'scope', 'stage', 'board']);   // stage / board: boards (docs/plans/boards.md) — where the card sits; a task's own subtask board   // qty / unit / catalogItemId: the quoted quantity and its price-book item (CFO 4.3)   // piece: { price, uid } — piece work (CFO phase 3); pay waits for an owner's approval in the books   // assignee: a member uid (opsMembers) or null

/**
 * Lock the quote: every line with quoted hours is Included and can't be tagged anything else (Lj, 11-01: "lock in scope
 * items as not extra"), and the plan starts now, so what's added later still gets sorted. Lines already picked as
 * extra, missed or overhead keep their pick and stay unlocked. → { writes, locked: n }
 */
export function lockScope(ctx, { projectId }) {
  const project = (ctx.projects || []).find((p) => p.id === projectId);
  if (!project) throw new Error(`no project ${projectId}`);
  const at = now(ctx);
  const lines = (ctx.tasks || []).filter((t) => t.projectId === projectId && !t.deletedAt && !t.bucket && !t.scopeLocked && Number(t.estimatedMinutes) > 0 && (!t.scope || t.scope === 'included'));
  const writes = lines.map((t) => update(`tasks/${t.id}`, { scope: 'included', billable: true, scopeLocked: true, updatedAt: at }));
  writes.push(update(`projects/${projectId}`, { planLockedAt: Number.isFinite(project.planLockedAt) ? project.planLockedAt : at, updatedAt: at }));
  return { writes, locked: lines.length };
}

/** Unlock one line (owners and admins: ctx.canUnlockScope false refuses; the UI hides it). */
export function unlockScope(ctx, { id }) {
  const task = need(ctx, id);
  if (ctx.canUnlockScope === false) throw new Error('only an owner or admin can unlock a quoted line');
  return { writes: [update(`tasks/${task.id}`, { scopeLocked: false, updatedAt: now(ctx) })] };
}

/** Extras went on an invoice: they stay on the job, out of what's left to bill. One group. */
export function markExtrasBilled(ctx, { ids = [], invoiceId }) {
  if (!invoiceId) throw new Error('which invoice?');
  const at = now(ctx);
  return { writes: ids.map((id) => need(ctx, id)).map((t) => update(`tasks/${t.id}`, { extraBilled: { invoiceId: String(invoiceId), at }, updatedAt: at })) };
}

/** Every task under `id` (children, their children…), in walk order. */
export function descendantIds(tasks, id) {
  const ids = [];
  const kids = (pid) => (tasks || []).filter((t) => t.parentId === pid);
  const walk = (pid) => kids(pid).forEach((k) => { if (k.id !== id && !ids.includes(k.id)) { ids.push(k.id); walk(k.id); } });
  walk(id);
  return ids;
}

/**
 * Partial update — only the changed fields, so concurrent edits elsewhere survive. Moving a task to
 * another project takes its subtasks along, and drops sections that belonged to the old project.
 */
export function updateTask(ctx, { id, ...patch }) {
  const task = need(ctx, id);
  const data = {};
  for (const [k, v] of Object.entries(patch)) if (EDITABLE.has(k) && v !== undefined) data[k] = v;
  if (!Object.keys(data).length) throw new Error('nothing to update');
  if (task.scopeLocked && 'scope' in data && (data.scope || null) !== (task.scope || null)) throw new Error('that line is locked in the quote — unlock it first');
  if (data.piece != null) {
    const price = Math.round(Number(data.piece.price) * 100) / 100;
    if (!(price > 0) || typeof data.piece.uid !== 'string' || !data.piece.uid) throw new Error('a piece needs a price and who does it');
    data.piece = { price, uid: data.piece.uid };
  }
  if (data.qty !== undefined && data.qty !== null) {
    const q = Number(data.qty);
    if (!Number.isFinite(q) || q < 0) throw new Error('qty must be a number, 0 or more');
    data.qty = q;
  }
  if (data.unit !== undefined) data.unit = data.unit ? String(data.unit).trim() || null : null;
  if (data.catalogItemId !== undefined) data.catalogItemId = data.catalogItemId ? String(data.catalogItemId) : null;
  const at = now(ctx);
  data.updatedAt = at;
  const writes = [];
  const moved = 'projectId' in data && (data.projectId || null) !== (task.projectId || null);
  if (moved) {
    if (!('sectionId' in data) && task.sectionId) data.sectionId = null;
    for (const kid of descendantIds(ctx.tasks, id)) writes.push(update(TASK(kid), { projectId: data.projectId, sectionId: null, updatedAt: at }));
  }
  return { writes: [update(TASK(id), data), ...writes], patch: data, moved: moved ? writes.length : 0 };
}

// ---- review: work handed back for the owner to check (Willow's queue, and anyone's) -------------------

/** Waiting for review: handed back and not yet approved or sent back. */
export const inReview = (t) => !!t?.review?.at && !t.review.decided && t.status !== 'done';   // an approved task reopened later isn't waiting again

/**
 * The review history (boards B6 — review-time task goals read it): `task.reviewLog: [{ at, by, decided, decidedAt }]`,
 * one round per hand-back; approve / send back stamp the open round ('approved' | 'sent'). Kept to the last 50.
 */
const reviewLogOf = (task) => (Array.isArray(task?.reviewLog) ? task.reviewLog.filter((r) => r && r.at) : (task?.review?.at ? [{ at: task.review.at, by: task.review.by || null, ...(task.review.decided ? { decided: task.review.decided, decidedAt: task.review.decidedAt || null } : {}) }] : []));
const appendReviewLog = (task, { at, by }) => [...reviewLogOf(task).map((r) => (r.decided ? r : { ...r, decided: 'replaced', decidedAt: at })), { at, by: by || null }].slice(-50);
function decideReviewLog(task, decided, at) {
  const log = reviewLogOf(task);
  let i = -1; for (let k = log.length - 1; k >= 0; k -= 1) if (!log[k].decided) { i = k; break; }
  if (i < 0) return [...log, { at: task.review?.at || at, by: task.review?.by || null, decided, decidedAt: at }].slice(-50);
  return log.map((r, k) => (k === i ? { ...r, decided, decidedAt: at } : r));
}

/** Hand work back for review: what was done, where the result is. The task stays open until approved. */
export function handBack(ctx, { id, note = '', by = null }) {
  const task = need(ctx, id);
  if (task.status === 'done') throw new Error(`${task.title} is already done`);
  const at = now(ctx);
  const text = String(note || '').trim();
  const who = by || ctx.by || null;
  const writes = [update(TASK(id), { review: { at, by: who, note: text || null }, reviewLog: appendReviewLog(task, { at, by: who }), updatedAt: at })];
  if (text) writes.push(...addTaskNote(ctx, { id, text: `Handed back: ${text}`, kind: 'review' }).writes);
  return { writes: mergeUpdates(writes) };
}

/** Approve handed-back work: it's done (completion stamped like any other), the review closes. */
export function approveReview(ctx, { id }) {
  const task = need(ctx, id);
  if (!inReview(task)) throw new Error(`${task.title} isn't waiting for review`);
  const r = completeTask(ctx, { id });
  const at = now(ctx);
  return { writes: mergeUpdates([...r.writes, update(TASK(id), { review: { ...task.review, decided: 'approved', decidedAt: at }, reviewLog: decideReviewLog(task, 'approved', at) })]) };
}

/** Send it back with what to change: open again, the note on the task, the assignee keeps it. */
export function sendBack(ctx, { id, note = '' }) {
  const task = need(ctx, id);
  if (!inReview(task)) throw new Error(`${task.title} isn't waiting for review`);
  const at = now(ctx);
  const text = String(note || '').trim();
  const writes = [update(TASK(id), { review: null, reviewLog: decideReviewLog(task, 'sent', at), updatedAt: at })];
  writes.push(...addTaskNote(ctx, { id, text: `Sent back${text ? `: ${text}` : ''}`, kind: 'review' }).writes);
  return { writes: mergeUpdates(writes) };
}

/** Append a free-form note; noteEvents carries when it was written (the Daily Log files notes by day). */
export function addTaskNote(ctx, { id, text, kind = null }) {
  const cur = need(ctx, id);
  const note = String(text || '').trim();
  if (!note) throw new Error('text required');
  const notes = [...(Array.isArray(cur.notes) ? cur.notes : []), note];
  const noteEvents = [...(Array.isArray(cur.noteEvents) ? cur.noteEvents : []), { text: note, at: now(ctx), sessionId: ctx.clock?.sessionId || null, ...(kind ? { kind } : {}) }];
  return { writes: [update(TASK(id), { notes, noteEvents })], notes };
}

/** Outline move within the project (planTaskMove); one group so the move lands whole. */
export function moveTask(ctx, { id, dir }) {
  const task = need(ctx, id);
  const scope = (ctx.tasks || []).filter((t) => t.projectId === task.projectId);
  const plan = planTaskMove(scope, (ctx.sections || []).filter((s) => s.projectId === task.projectId), id, dir);
  const at = now(ctx);
  return { writes: plan.map((w) => update(TASK(w.id), { ...w.patch, updatedAt: at })), moved: plan.length > 0 };
}

/** Hard delete (the app's behaviour); `tree` also removes every descendant. */
export function deleteTask(ctx, { id, tree = true }) {
  need(ctx, id);
  const ids = [id];
  if (tree) {
    const kids = (pid) => (ctx.tasks || []).filter((t) => t.parentId === pid);
    const walk = (pid) => kids(pid).forEach((k) => { if (!ids.includes(k.id)) { ids.push(k.id); walk(k.id); } });
    walk(id);
  }
  return { writes: ids.map((x) => del(TASK(x))), ids };
}

/** Done → back to todo. */
export function reopenTask(ctx, { id }) {
  const t = need(ctx, id);
  if (t.status === 'todo') throw new Error(`${t.title} is already open`);
  if (isVoidTask(t)) return restoreTaskWrites(ctx, { id, at: now(ctx) });   // a void task (closed with its project) comes back as it was
  return { writes: [update(TASK(id), { status: 'todo', completedAt: null, completion: null, updatedAt: now(ctx) })] };
}

// ---- banking time ----------------------------------------------------------------------

// A time entry for the stretch that just ended (`elapsed` ms up to now).
export function buildTimeEntry(elapsed, at, newId) {
  const startMs = at - elapsed;
  const sd = new Date(startMs); const ed = new Date(at);
  const p = (n) => String(n).padStart(2, '0');
  return { id: newId(), date: localDateStr(startMs), startTime: `${p(sd.getHours())}:${p(sd.getMinutes())}`, endTime: `${p(ed.getHours())}:${p(ed.getMinutes())}`, duration: elapsed, note: null, manual: false, createdAt: at };
}

/** Task patch + session patch for banking `elapsed` onto a task (+ `extra` fields on the task). */
function bankWrites(ctx, { taskId, elapsed = 0, pauseTime = 0, sessionId = null, timeEntry = null }, extra = {}) {
  const task = need(ctx, taskId);
  const patch = { ...extra };
  if (elapsed > 0) {
    patch.timeSpent = (task.timeSpent || 0) + elapsed;
    patch.pauseTime = (task.pauseTime || 0) + pauseTime;
  }
  // D5: in department mode each entry carries the department its task runs under (workdept.js).
  if (timeEntry && !(task.timeEntries || []).some((e) => e.id === timeEntry.id)) patch.timeEntries = [...(task.timeEntries || []), stampEntryDepartment(timeEntry, task, ctx)];
  const writes = [];
  if (Object.keys(patch).length) writes.push(update(TASK(taskId), patch));
  if (sessionId && elapsed > 0) {
    const s = (ctx.sessions || []).find((x) => x.id === sessionId);
    if (s) writes.push(update(`clockSessions/${sessionId}`, { focusedTime: (s.focusedTime || 0) + elapsed }));
  }
  return { writes, task };
}

const freshTimer = (at) => makeTimerState({ startedAt: at });

/**
 * What the open work session is on right now — the task being timed (and a break) — so the Team page
 * can show "Pat · Joy's farmstand · Framing the north wall". Only when clocked in and the session is known.
 */
export function sessionTask(ctx, patch) {
  const sid = ctx.clock?.active ? ctx.clock.sessionId : null;
  if (!sid || !(ctx.sessions || []).some((s) => s.id === sid)) return [];
  return [update(`clockSessions/${sid}`, patch)];
}
const LOCK_OFF = { active: false, taskId: null };

// The order work actually got done in, stamped on the task at completion:
//   index        nth task completed in its project (all time)
//   dayIndex     nth task completed today (across projects)
//   plannedIndex the outline label it had at that moment ("2.3"); plannedRank = that label's
//                position among the project's open roots (1 = it was the next planned step)
export function completionStamp(ctx, task) {
  const at = now(ctx);
  const tasks = ctx.tasks || [];
  const dayStart = new Date(localDateStr(at) + 'T00:00:00').getTime();
  const done = tasks.filter((t) => t.id !== task.id && t.status === 'done' && !t.bucket);
  const index = task.projectId ? done.filter((t) => t.projectId === task.projectId).length + 1 : null;
  const dayIndex = done.filter((t) => (t.completedAt || 0) >= dayStart).length + 1;
  let plannedIndex = null, plannedRank = null;
  if (task.projectId) {
    const projTasks = tasks.filter((t) => t.projectId === task.projectId);
    const secs = (ctx.sections || []).filter((s) => s.projectId === task.projectId);
    const idx = computeTaskIndices(projTasks, secs);
    plannedIndex = idx.get(task.id) || null;
    const openRoots = projTasks.filter((t) => !t.parentId && !t.bucket && t.status !== 'done' && !isVoidTask(t));
    const labels = openRoots.map((t) => idx.get(t.id)).filter(Boolean).sort((a, b) => {
      const pa = a.split('.').map(Number), pb = b.split('.').map(Number);
      for (let i = 0; i < Math.max(pa.length, pb.length); i++) { const d = (pa[i] ?? 0) - (pb[i] ?? 0); if (d) return d; }
      return 0;
    });
    const pos = plannedIndex ? labels.indexOf(plannedIndex) : -1;
    plannedRank = pos >= 0 ? pos + 1 : null;
  }
  return { index, dayIndex, plannedIndex, plannedRank, at };
}

/** Section fully done → completed; the next locked section opens and its blocked tasks free up. */
export function completeSectionWrites(ctx, { sectionId, projectId, doneTaskId = null }) {
  const tasks = ctx.tasks || [];
  const roots = tasks.filter((t) => t.sectionId === sectionId && !t.parentId);
  const isDone = (t) => t.status === 'done' || isVoidTask(t) || t.id === doneTaskId;
  if (!roots.length || !roots.every(isDone)) return { writes: [], unlockedSectionId: null };
  const at = now(ctx);
  const writes = [update(`sections/${sectionId}`, { status: 'completed', completedAt: at })];
  const next = (ctx.sections || []).filter((s) => s.projectId === projectId).sort((a, b) => (a.order || 0) - (b.order || 0)).find((s) => s.status === 'locked');
  if (next) {
    writes.push(update(`sections/${next.id}`, { status: 'active' }));
    tasks.filter((t) => t.sectionId === next.id && t.status === 'blocked' && !t.blockedByTaskId).forEach((t) => writes.push(update(TASK(t.id), { status: 'todo' })));
  }
  return { writes, unlockedSectionId: next ? next.id : null };
}

/**
 * Complete a task: bank the timer's time (elapsed / timeEntry decided by the caller via bankable()),
 * stamp completion, reset lock + timer if this task held them, unblock dependants, book the
 * expense/income doc, and close the section when it was the last open step. ONE group.
 * Metrics (CFO 4.4): `captures` [{ metricId, value }] are logged on the task in the same group; `ask` = the template
 * metrics still to ask for (source 'ask', no value, not answered by the quote) — the completion sheet shows those.
 */
export function completeTask(ctx, { id, elapsed = 0, pauseTime = 0, sessionId = null, timeEntry = null, captures = [] }) {
  const pre = need(ctx, id);
  if (pre.status === 'done') throw new Error(`${pre.title} is already done`);
  const at = now(ctx);
  const completion = pre.bucket ? null : { ...completionStamp(ctx, pre), sessionId: sessionId || null };
  const { writes, task } = bankWrites(ctx, { taskId: id, elapsed, pauseTime, sessionId, timeEntry }, { status: 'done', completedAt: at, ...(completion ? { completion } : {}) });
  if (ctx.lock?.taskId === id) {
    writes.push(set('meta/lock', LOCK_OFF));
    writes.push(set('meta/timer', freshTimer(at)));
  }
  if (ctx.timer?.taskId === id) writes.push(...sessionTask(ctx, { currentTaskId: null, currentPause: null }));
  (ctx.tasks || []).filter((t) => t.blockedByTaskId === id).forEach((t) => writes.push(update(TASK(t.id), { status: 'todo', blockedByTaskId: null })));
  if (task.taskType === 'expense' || task.taskType === 'income') {
    writes.push(set(`expenses/task_${id}`, {
      id: `task_${id}`, type: task.taskType, title: task.title || '', amount: task.moneyEarned || 0,
      category: task.expenseCategory || 'other', date: localToday(), projectId: task.projectId || null, taskId: id,
      vendorId: null, accountId: null, receiptUrl: null, receiptThumbnail: null, ocrData: null,
      notes: task.description || '', assignedTo: null, reimbursable: false, createdAt: at, updatedAt: at,
    }));
  }
  let unlockedSectionId = null;
  if (task.sectionId && task.projectId) {
    const sec = completeSectionWrites(ctx, { sectionId: task.sectionId, projectId: task.projectId, doneTaskId: id });
    writes.push(...sec.writes);
    unlockedSectionId = sec.unlockedSectionId;
  }
  const cap = captures?.length ? captureWrites(ctx, { task: pre, captures }) : { writes: [], values: [] };
  writes.push(...cap.writes);
  const ask = askOnComplete(pre, ctx.metrics || [], [...(ctx.metricValues || []), ...cap.values]);
  return { writes, completion, unlockedSectionId, task, captured: cap.values, ask };
}

/**
 * Stop timing a task without completing it: bank time, optionally record a skip, and reset the
 * timer / lock unless they belong to another task (`keepTimer` / `keepLock`).
 */
export function stopTask(ctx, { id, elapsed = 0, pauseTime = 0, sessionId = null, timeEntry = null, skip = false, keepTimer = false, keepLock = false }) {
  const task = need(ctx, id);
  const at = now(ctx);
  const extra = skip ? { skippedCount: (task.skippedCount || 0) + 1, lastSkippedAt: at } : {};
  const { writes } = bankWrites(ctx, { taskId: id, elapsed, pauseTime, sessionId, timeEntry }, extra);
  if (!keepTimer) writes.push(set('meta/timer', freshTimer(at)), ...sessionTask(ctx, { currentTaskId: null, currentPause: null }));
  if (!keepLock) writes.push(set('meta/lock', LOCK_OFF));
  return { writes };
}

export const skipTask = (ctx, input) => stopTask(ctx, { ...input, skip: true });
